import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useUser } from "@/contexts/UserContext";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

type DemoStep = {
  title: string;
  content: React.ReactNode;
  position?: "left" | "right" | "center" | "bottom";
};

export default function FirstTimeUserDemo() {
  const [showDemo, setShowDemo] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { user, updateUserProfile } = useUser();

  useEffect(() => {
    // Only show demo for first-time users who just completed onboarding
    // and haven't seen the demo yet (hasSeenDemo is explicitly false)
    if (user && user.isNewUser === false && user.hasSeenDemo === false) {
      setShowDemo(true);
    }
  }, [user]);

  const handleDemoComplete = async () => {
    setShowDemo(false);
    // Mark demo as seen in user profile
    if (user) {
      await updateUserProfile({ hasSeenDemo: true });
    }
  };

  const handleSkip = async () => {
    handleDemoComplete();
  };

  const handleNext = () => {
    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleDemoComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const demoSteps: DemoStep[] = [
    {
      title: "Welcome to Step",
      content: (
        <div className="space-y-4">
          <p>
            Welcome to Step! Let's take a quick tour of the app to help you get
            started.
          </p>
          <p>
            Step helps you track your wellbeing, collaborate with your team, and
            reach your goals effectively.
          </p>
        </div>
      ),
      position: "center",
    },
    {
      title: "Daily Check-Ins",
      content: (
        <div className="space-y-4">
          <p>
            Start your day with a quick check-in to track your mood, energy
            levels, and focus.
          </p>
          <p>
            Your team can see your status, making it easier to collaborate
            effectively.
          </p>
          <p>Access check-ins from the sidebar or the dashboard.</p>
        </div>
      ),
      position: "left",
    },
    {
      title: "Dashboard",
      content: (
        <div className="space-y-4">
          <p>
            Your dashboard gives you an overview of your team's status and your
            personal metrics.
          </p>
          <p>
            You can switch between different views using the tabs at the top.
          </p>
          <p>Notice the quick actions and insights tailored to your role.</p>
        </div>
      ),
      position: "center",
    },
    {
      title: "Team Chat",
      content: (
        <div className="space-y-4">
          <p>Use the chat feature to communicate with your team members.</p>
          <p>You can start individual or group conversations.</p>
          <p>Look for the chat icon in the bottom left of your screen.</p>
        </div>
      ),
      position: "bottom",
    },
    {
      title: "Goal Tracking",
      content: (
        <div className="space-y-4">
          <p>Set and track goals for yourself and your team.</p>
          <p>
            Break down goals into milestones and track your progress over time.
          </p>
          <p>Access goals from your profile or the team section.</p>
        </div>
      ),
      position: "right",
    },
    {
      title: "Need Help?",
      content: (
        <div className="space-y-4">
          <p>
            If you need help at any time, click the help icon in the bottom
            right corner.
          </p>
          <p>
            You can also access the user guide from the dashboard for detailed
            information.
          </p>
          <p>You're all set to start using Step!</p>
        </div>
      ),
      position: "right",
    },
  ];

  if (!showDemo) return null;

  return (
    <Dialog open={showDemo} onOpenChange={setShowDemo}>
      <DialogContent
        className="sm:max-w-md p-0 bg-transparent border-none shadow-none"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <Card className="border shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              {demoSteps[currentStep].title}
            </CardTitle>
          </CardHeader>
          <CardContent>{demoSteps[currentStep].content}</CardContent>
          <CardFooter className="flex justify-between border-t pt-4">
            <div>
              {currentStep > 0 ? (
                <Button variant="outline" size="sm" onClick={handleBack}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleSkip}>
                  Skip Tour
                </Button>
              )}
            </div>
            <Button onClick={handleNext} size="sm">
              {currentStep < demoSteps.length - 1 ? (
                <>
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              ) : (
                <>
                  Got it!
                  <Check className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
